/* eslint-disable no-unused-vars */
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import styled from 'styled-components';
import { Palette, routes } from 'config';
import { useState, useId, useRef, useEffect, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getIsLocalHost, getCookieValue } from 'utils';
import { Loader, Separator, CustomInput } from 'components';
import { ClearSearchInput } from 'assets/icons';
import AppnextHeaderLogo from 'assets/images/appnext_header_logo.svg';
import { LoginPublisherList, SelectedPublisherList } from 'views/loginPages';
import { authAPI } from 'services/newPlatformAuth.service';
import { authActions } from 'store/auth/auth';
import { authGoogleClientId, authGoogleKey } from 'constants';

export const AdminLoginComponent = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [isGoogleLoginState, setIsGoogleLogin] = useState(() => {
    const isGoogleLoginTime = +getCookieValue(authGoogleKey);
    const currentTime = new Date().getTime();
    if (currentTime <= isGoogleLoginTime) {
      return true;
    } else {
      document.cookie = `${authGoogleKey}=; domain=.${window.location.hostname}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT;`;
      return false;
    }
  });

  const [errorMessage, setErrorMessage] = useState('');
  const [googleLoginAction, { isError, isLoading, isSuccess }] = authAPI.useGoogleLoginMutation();
  const isDefaultErrorRef = useRef(false);
  const authError = useSelector((state) => state.auth.authError);
  const authType = useSelector((state) => state.auth.authType);
  const publisherList = useSelector((state) => state.auth.publisherList);
  const isLocalHost = getIsLocalHost();

  const [token, setToken] = useState('');
  const inputId = useId();

  const handleClear = () => {
    isDefaultErrorRef.current = false;
    setToken('');
    setErrorMessage('');
  };

  const handleInput = (event) => {
    isDefaultErrorRef.current = false;
    const token = event.target.value;
    setToken(token);
    setErrorMessage('');
  };

  const handleLoginSuccess = async (credentialResponse) => {
    console.log({ credentialResponse });
    const idToken = credentialResponse.credential;
    // Send ID token to your .NET Core server
    const res = await googleLoginAction({
      idToken,
      companyKey: token,
    });
    if (res?.error) {
      const errorMsg = res.error.data.Message || res.error.data;
      setErrorMessage(errorMsg);
      console.error('Error sending token to server:', res?.error?.data?.title);
    }
    if (res?.data) {
      const expiryDate = new Date(res.data.cookiesExpiaryDuration).toUTCString();
      console.log({ expiryDate });
      document.cookie = `${authGoogleKey}=${res.data.cookiesExpiaryDuration.toString()}; domain=.${
        window.location.hostname
      }; path=/; expires=${expiryDate};`;
      setIsGoogleLogin(true);
    }
  };

  //const expiryDate = new Date(1737468227214).toUTCString();
  //document.cookie = `${authGoogleKey}=${1737468227214};domain=.localhost; path=/; expires=${expiryDate};`;

  const handleLoginFailure = (error) => {
    console.error('Google Login Failed:', error);
  };

  // useEffect(() => {
  //   window.google.accounts.id.initialize({
  //     callback: (response) => {
  //       if (response.credential) {
  //         handleLoginSuccess(response);
  //       } else {
  //         handleLoginFailure('No credential returned.');
  //       }
  //     },
  //   });
  // }, []);

  useEffect(() => {
    let retryTimeout;
    const initializeGoogle = () => {
      if (window.google && window.google.accounts && window.google.accounts.id) {
        window.google.accounts.id.initialize({
          callback: (response) => {
            if (response.credential) {
              handleLoginSuccess(response);
            } else {
              handleLoginFailure('No credential returned.');
            }
          },
        });
      } else {
        console.warn('Google API not loaded yet, retrying...');
        retryTimeout = setTimeout(initializeGoogle, 100);
      }
    };
    initializeGoogle();
    return () => {
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
    };
  }, []);

  useLayoutEffect(() => {
    dispatch(authActions.changeAuthType('admin'));
    // if (rememberMe === null) {
    //   dispatch(authActions.changeRememberMe(false));
    // }
  }, []);

  const getPublisherLink = (pb_number) => {
    const { host, protocol, pathname } = window.location;

    if (!pathname.toLocaleLowerCase().includes('/adminlogin')) {
      alert(`Something is wrong with your URL: ${window.location.origin}`);
      return null;
    }
    let newPathName = pathname.toLocaleLowerCase().replace('/adminlogin', '/');
    const newDomain = `${protocol}//${pb_number}.${host}${newPathName}`;
    console.log(newDomain);
    return newDomain;
  };

  if (isGoogleLoginState) {
    return (
      <LoginPublisherList
        authGoogleKey={authGoogleKey}
        setIsGoogleLogin={setIsGoogleLogin}
        setToken={setToken}
        getPublisherLink={getPublisherLink}
      >
        <SelectedPublisherList getPublisherLink={getPublisherLink} />
      </LoginPublisherList>
    );
  }

  return (
    <Box>
      {isLoading && <Loader.Global delay={100} />}
      <StyledForm id='login'>
        <FlexCenter>
          <Label>Login</Label>
          <img
            src={AppnextHeaderLogo}
            alt='Appnext Logo'
            style={{ height: '33px', width: 'auto' }}
          />
        </FlexCenter>
        <Separator width='100%' marginLeft='0' style={{ marginBottom: '24px' }} />
        <CustomInput
          name='AdminLogin'
          onChange={handleInput}
          //error={authError === 'Rejected' && isDefaultErrorRef.current}
          error={Boolean(errorMessage)}
          errorText={errorMessage}
          value={token}
          rightIcon={<ClearSearchInput />}
          rightIconOnClick={handleClear}
          id={inputId}
          topLabel='Company Key:'
          containerStyle={{ height: '74px', marginBottom: '24px' }}
        />
        <BtnBox isHidden={!token}>
          <GoogleLogin
            onSuccess={handleLoginSuccess}
            onError={handleLoginFailure}
            text='signin_with'
            size='large'
            theme='outline'
            width='303'
            locale='en'
          />
          {!token && <Slice />}
        </BtnBox>
      </StyledForm>
    </Box>
  );
};

export const AdminLogin = () => {
  return (
    <GoogleOAuthProvider clientId={authGoogleClientId}>
      <AdminLoginComponent />
    </GoogleOAuthProvider>
  );
};

const Slice = styled.div`
  background: #fff;
  opacity: 0.4;
  height: 40px;
  width: 303px;
  border-radius: 4px;
  position: absolute;
  top: 0;
  cursor: not-allowed;
`;
const BtnBox = styled.div`
  position: relative;
`;

const Box = styled.div`
  display: flex;
  justify-content: space-evenly;
  align-items: center;
  height: 90vh;
`;

const FlexCenter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const Label = styled.p`
  font-size: 40px;
  color: ${Palette.PrimaryGray._900};
  font-weight: 700;
  letter-spacing: -0.8px;
  line-height: 48px;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  width: 400px;
  height: 297px;
  background: #fff;
  padding: 32px 48px;
  border-radius: 8px;
  box-shadow: 0px 1px 3px 0px rgba(30, 50, 70, 0.1), 0px 1px 2px 0px rgba(30, 50, 70, 0.06);
`;
